/**
 * Typed error model. `ApiError` is thrown for every non-2xx response and for
 * network failures, so all UI error handling switches on a single shape.
 * Callers switch on `code` (e.g. a screen renders a dedicated "not authorized"
 * state on `FORBIDDEN`).
 *
 * This is the HTTP-status-derived code set. Fine-grained backend codes from
 * wsr-api's error envelope (`{ code, message, status }`) are carried through on
 * `details.code` (lifted by `client.ts`) so they remain reachable without
 * widening this union; extend the union here only when the FE needs to branch
 * on a status-level code the transport itself surfaces.
 */
export type ErrorCode =
  | 'NETWORK_ERROR'
  | 'SESSION_EXPIRED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

export interface ApiErrorInit {
  requestId?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, status: number, init: ApiErrorInit = {}) {
    super(`${code} (${status})`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = init.requestId;
    this.details = init.details;
  }
}

/** Map an HTTP status to an `ErrorCode` for responses with no usable body. */
export function statusToCode(status: number): ErrorCode {
  if (status === 400) return 'VALIDATION';
  if (status === 401) return 'SESSION_EXPIRED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  return 'UNKNOWN';
}
